import React, { useState, useEffect } from "react";
import axios from "axios";
import "./GestionCaissiers.css";

const GestionCaissiers = () => {
  const [cashiers, setCashiers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form states
  const [name, setName] = useState("");
  const [pinCode, setPinCode] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [senderEmailPassword, setSenderEmailPassword] = useState("");
  const [senderWhatsapp, setSenderWhatsapp] = useState("56332255");
  const [editingId, setEditingId] = useState(null);
  const [msg, setMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Chargement des caissiers et des emplacements
  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorised: token };

      const [usersRes, locsRes] = await Promise.all([
        axios.get("/api/auth/AllUser", { headers }),
        axios.get("/api/Locations/getAllLocations", { headers }),
      ]);

      const rawUsers = usersRes.data?.AllUser || (Array.isArray(usersRes.data) ? usersRes.data : []);
      setCashiers(rawUsers);

      const locs = Array.isArray(locsRes.data) ? locsRes.data : [];
      setLocations(locs);
      if (locs.length > 0 && !selectedLocation) {
        setSelectedLocation(locs[0]._id);
      }
    } catch (err) {
      console.error("Erreur chargement caissiers :", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setName("");
    setPinCode("");
    setSenderEmail("");
    setSenderEmailPassword("");
    setSenderWhatsapp("56332255");
    setEditingId(null);
    setMsg("");
    setErrorMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg("");
    setErrorMsg("");

    if (!name || !pinCode) {
      setErrorMsg("Le nom et le code PIN (4 chiffres) sont obligatoires.");
      return;
    }

    if (pinCode.length !== 4) {
      setErrorMsg("Le code PIN doit comporter exactement 4 chiffres.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const headers = { Authorised: token };

      if (editingId) {
        // Mise à jour caissier
        const cleanPassword = pinCode.length === 4 ? pinCode + "0000" : pinCode;
        await axios.put(
          `/api/auth/UpdateUser/${editingId}`,
          {
            name,
            pinCode,
            password: cleanPassword,
            localisation: selectedLocation,
            senderEmail,
            senderEmailPassword,
            senderWhatsapp: senderWhatsapp || "56332255",
          },
          { headers }
        );
        setMsg("✅ Caissier et ses paramètres d'envoi mis à jour avec succès !");
      } else {
        // Création d'un nouveau caissier rapide avec Code PIN à 4 chiffres
        const cleanMatricule = "CAISS-" + Math.floor(1000 + Math.random() * 9000);
        const cleanEmail = name.toLowerCase().replace(/[^a-z0-9]/gi, ".") + Math.floor(100 + Math.random() * 900) + "@caisse.tn";
        const cleanPassword = pinCode.length === 4 ? pinCode + "0000" : pinCode;

        await axios.post("/api/auth/SignUp", {
          name,
          email: cleanEmail,
          password: cleanPassword,
          matricule: cleanMatricule,
          localisation: selectedLocation,
          pinCode,
          senderEmail,
          senderEmailPassword,
          senderWhatsapp: senderWhatsapp || "56332255",
          role: "user",
        });

        setMsg("✅ Caissier créé avec succès !");
      }

      resetForm();
      fetchData();
    } catch (err) {
      console.error("Erreur enregistrement caissier :", err);
      setErrorMsg(
        err.response?.data?.errors?.[0]?.msg ||
          err.response?.data?.message ||
          err.response?.data?.msg ||
          "Erreur lors de l'enregistrement."
      );
    }
  };

  const handleEdit = (cashier) => {
    setEditingId(cashier._id);
    setName(cashier.name || "");
    setPinCode(cashier.pinCode || "");
    setSenderEmail(cashier.senderEmail || "");
    setSenderEmailPassword(cashier.senderEmailPassword || "");
    setSenderWhatsapp(cashier.senderWhatsapp || "56332255");

    if (cashier.localisation) {
      setSelectedLocation(
        typeof cashier.localisation === "object"
          ? cashier.localisation._id
          : cashier.localisation
      );
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleRevokeCaisse = async (id, cashierName) => {
    if (
      !window.confirm(
        `Voulez-vous retirer l'accès caisse de "${cashierName}" ? (Le compte utilisateur et ses données restent conservés en base de données).`
      )
    )
      return;
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `/api/auth/revokeCaisse/${id}`,
        {},
        {
          headers: { Authorised: token },
        }
      );
      setMsg(`✅ Accès caisse retiré pour "${cashierName}". Le compte utilisateur est conservé.`);
      fetchData();
    } catch (err) {
      setErrorMsg("Erreur lors du retrait de l'accès caisse.");
    }
  };

  const getLocationName = (locInput) => {
    if (!locInput) return "Toutes caisses";
    if (typeof locInput === "object" && locInput.name) return locInput.name;
    const found = locations.find((l) => l._id === locInput || String(l._id) === String(locInput));
    return found ? found.name : locInput;
  };

  return (
    <div className="gestion-caissiers-container">
      <div className="gestion-caissiers-header">
        <h2>👤 Création & Configuration des Caissiers & Canaux de Reçu</h2>
        <p>Définissez le Nom, le Code PIN (4 chiffres), la Boutique, ainsi que l'Email et le N° WhatsApp d'envoi des reçus.</p>
      </div>

      {/* Formulaire de création / édition rapide */}
      <div className="caissier-card-form">
        <h3>{editingId ? "✏️ Modifier la Configuration du Caissier" : "➕ Ajouter un Nouveau Caissier"}</h3>

        {msg && <div className="alert-success-box">{msg}</div>}
        {errorMsg && <div className="alert-error-box">{errorMsg}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>Nom du Caissier *</label>
              <input
                type="text"
                placeholder="ex: Sarah Ben Ali"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label>Code PIN à 4 chiffres (Mot de passe Caisse) *</label>
              <input
                type="text"
                maxLength={4}
                placeholder="ex: 1234"
                value={pinCode}
                onChange={(e) => setPinCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
                required
              />
            </div>

            <div className="form-group">
              <label>Localisation / Caisse POS Attribuée *</label>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
              >
                {locations.length > 0 ? (
                  locations.map((loc) => (
                    <option key={loc._id} value={loc._id}>
                      {loc.name}
                    </option>
                  ))
                ) : (
                  <>
                    <option value="Bag n Go">Bag n Go</option>
                    <option value="Twin Park">Twin Park</option>
                    <option value="E-Space">E-Space</option>
                    <option value="Spintoss">Spintoss</option>
                    <option value="Wellness">Wellness</option>
                  </>
                )}
              </select>
            </div>

            <div className="form-group">
              <label>💬 N° WhatsApp Émetteur de la Caisse</label>
              <input
                type="text"
                placeholder="ex: 56332255"
                value={senderWhatsapp}
                onChange={(e) => setSenderWhatsapp(e.target.value.trim())}
              />
            </div>

            <div className="form-group">
              <label>📧 Email d'envoi du Reçu (Facultatif)</label>
              <input
                type="email"
                placeholder="ex: tuniscity@taskopos.tn"
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value.trim())}
              />
            </div>

            <div className="form-group">
              <label>🔑 Mot de Passe SMTP Email (Facultatif)</label>
              <input
                type="password"
                placeholder="••••••••"
                value={senderEmailPassword}
                onChange={(e) => setSenderEmailPassword(e.target.value)}
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary-caissier">
              {editingId ? "Enregistrer les modifications" : "Créer le Caissier"}
            </button>
            {editingId && (
              <button type="button" onClick={resetForm} className="btn-cancel-caissier">
                Annuler
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Liste des caissiers enregistrés */}
      <div className="caissiers-list-container">
        <h3>📋 Liste des Caissiers & Canaux de Reçu ({cashiers.length})</h3>

        {loading ? (
          <p style={{ textAlign: "center" }}>⏳ Chargement...</p>
        ) : cashiers.length === 0 ? (
          <p style={{ textAlign: "center", color: "#888" }}>Aucun caissier enregistré.</p>
        ) : (
          <table className="caissiers-table">
            <thead>
              <tr>
                <th>Nom Caissier</th>
                <th>Code PIN</th>
                <th>Boutique POS</th>
                <th>N° WhatsApp Émetteur</th>
                <th>Email d'envoi</th>
                <th>Lien Direct Caisse</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cashiers.map((c) => {
                const locName = getLocationName(c.localisation);
                const directUrl = `${window.location.origin}/CaisseMobile?location=${encodeURIComponent(locName)}`;
                return (
                  <tr key={c._id}>
                    <td style={{ fontWeight: "bold" }}>👤 {c.name}</td>
                    <td>
                      <span className="badge-pin">🔑 {c.pinCode || "****"}</span>
                    </td>
                    <td>
                      <span className="badge-location">📍 {locName}</span>
                    </td>
                    <td>
                      <span className="badge-wa">💬 {c.senderWhatsapp || "56332255"}</span>
                    </td>
                    <td>
                      <span className="badge-email">{c.senderEmail || "Standard"}</span>
                    </td>
                    <td>
                      <a
                        href={directUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link-direct-pos"
                      >
                        📱 Caisse ({locName})
                      </a>
                    </td>
                    <td>
                      <button onClick={() => handleEdit(c)} className="btn-edit-sm">
                        ✏️ Éditer
                      </button>
                      <button onClick={() => handleRevokeCaisse(c._id, c.name)} className="btn-delete-sm">
                        🚫 Retirer caisse
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default GestionCaissiers;
